import React, { useState } from 'react';
import { Container, Button, Form } from 'react-bootstrap';
import QuestionField from './QuestionField';
import DateField from './DateField';
import CheckboxField from './CheckboxField';
import DemographicForm from './DemoForms/DemographicForm';
import {
  calculateAge,
  generateUniqueId,
  getAcuitylinknew,
  readyTobookEligibity,
  saveIntakeQuestionnaireDataNew,
  useStateWithSentry
} from '../../utilities/common';
import { useTracking } from '../../context/TrackingContext';
import DemographicPartnerForm from './DemoForms/DemographicPartnerForm';
import AdditionalInformation from './DemoForms/AdditionalInformation';
import TermAndCondition from './DemoForms/TermAndCondition';
import { validateAndScroll } from '../intake_new/intake-utilities';
import { useLoading } from '../../context/LoadingContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Sentry from '@sentry/react';

function ProgressDots({ total = 6, activeIndex = 0 }) {
  return (
    <div className="progress-dots">
      {Array.from({ length: total }).map((_, idx) => (
        <span
          key={idx}
          className={`dot ${idx === activeIndex ? 'active' : ''}`}
        ></span>
      ))}
    </div>
  );
}

const getTreatmentInterestOptions = tryingToGetPregnant => {
  if (tryingToGetPregnant === 'Yes') {
    return [
      { label: 'IVF — In-Vitro Fertilization', value: 'ivf' },
      {
        label:
          'Lower-Intervention Care — IUI, Cycle Monitoring, Intercourse Timing',
        value: 'lower_intervention'
      },
      {
        label:
          "Not Sure — We'll help you decide the best place to start",
        value: 'not_sure'
      }
    ];
  }

  if (tryingToGetPregnant === 'No') {
    return [
      {
        label:
          'Fertility Check Only — Learn about your egg or sperm count',
        value: 'fertility_check'
      },
      {
        label: 'Freeze Eggs — Freeze your eggs for the future',
        value: 'freeze_eggs'
      },
      {
        label:
          'Freeze Embryos — Create and freeze embryos with partner or donor sperm',
        value: 'freeze_embryos'
      },
      {
        label:
          "Not Sure — We'll help you decide the best place to start",
        value: 'not_sure'
      }
    ];
  }

  return [];
};

function SampleFormContainer() {
  const [formData, setFormData] = useStateWithSentry('formData', {
    trying_to_get_pregnant: null,
    treatment_interest: null,
    location: null,
    dob: null,
    valid_ohip: null,
    toronto_acknowledge_ncp_simcoe: [],
    toronto_acknowledge_vr_ottawa: [],
    toronto_acknowledge_vr_simcoe: [],
    eligibility: null
  });

  const [currentKey, setCurrentKey] = useState('trying_to_get_pregnant');
  const [history, setHistory] = useState([]);

  const [showFormErrors, setShowFormErrors] = useState(false);

  const { setIsLoading } = useLoading();

  const userTracking = useTracking();

  const blobPath = `${import.meta.env.VITE_RAW_FILE_PATH}`;
  const specificBlobName = generateUniqueId();

  const dependentKeys = {
    trying_to_get_pregnant: [
      'treatment_interest',
      'location',
      'valid_ohip',
      'dob',
      'toronto_acknowledge_ncp_simcoe',
      'toronto_acknowledge_vr_ottawa',
      'toronto_acknowledge_vr_simcoe',
      'eligibility'
    ],
    treatment_interest: [
      'location',
      'valid_ohip',
      'dob',
      'toronto_acknowledge_ncp_simcoe',
      'toronto_acknowledge_vr_ottawa',
      'toronto_acknowledge_vr_simcoe',
      'eligibility'
    ],
    location: [
      'valid_ohip',
      'dob',
      'toronto_acknowledge_ncp_simcoe',
      'toronto_acknowledge_vr_ottawa',
      'toronto_acknowledge_vr_simcoe',
      'eligibility'
    ],
    valid_ohip: [
      'dob',
      'toronto_acknowledge_ncp_simcoe',
      'toronto_acknowledge_vr_ottawa',
      'toronto_acknowledge_vr_simcoe',
      'eligibility'
    ],
    dob: [
      'toronto_acknowledge_ncp_simcoe',
      'toronto_acknowledge_vr_ottawa',
      'toronto_acknowledge_vr_simcoe',
      'eligibility'
    ],
    toronto_acknowledge_ncp_simcoe: [],
    toronto_acknowledge_vr_ottawa: [],
    toronto_acknowledge_vr_simcoe: [],
    acuity_link: [],
    demographic_form: []
  };

  const questions = {
    trying_to_get_pregnant: {
      type: 'choice',
      title: 'Are you trying to get pregnant right now?',
      options: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' }
      ]
    },
    location: {
      type: 'choice',
      title: 'Which location are you closest to?',
      options: [
        { label: 'Toronto', value: 'Toronto' },
        { label: 'Ottawa', value: 'Ottawa' },
        { label: 'Simcoe County', value: 'Simcoe' },
        { label: 'I live out of province', value: 'Out of Province' }
      ]
    },
    valid_ohip: {
      type: 'choice',
      title: 'Do you have a valid Ontario Health Insurance Plan (OHIP) card?',
      options: [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' }
      ]
    },
    dob: { type: 'date', title: 'What is your date of birth?' },
    toronto_acknowledge_ncp_simcoe: {
      type: 'checkbox',
      title: `To get started, we’ll meet for a 30-minute initial consultation at our Toronto location (160 Bloor Street East). After the appointment, you will be able to access virtual care and cycle monitoring in Simcoe County.<br/>
          Please note that some testing and treatments are only provided at our Toronto office. Our team can clarify which ones apply to you during your appointment. `,
      options: [
        {
          label:
            'I understand I will need to visit the Toronto location for some testing and treatment.',
          value: 'acknowledge_toronto'
        }
      ]
    },
    toronto_acknowledge_vr_ottawa: {
      type: 'checkbox',
      title: `To see a specialist, we require a referral from your Family Doctor or Nurse Practitioner. If you do not have a primary care provider or are unable to access a referral from them in a timely fashion, 
      you may book a Virtual Referral with our Nurse Practitioner on the next page. Seeing our Nurse Practitioner will not negatively impact your primary care provider.<br/>   
    Please note that for egg freezing and IVF, some testings and treatments are only available at our Toronto office. Our team can clarify which ones apply to you during your Virtual Referral appointment.`,
      options: [
        {
          label:
            'I understand I will need to visit the Toronto location for some testing and treatment.',
          value: 'acknowledge_toronto'
        }
      ]
    },
    toronto_acknowledge_vr_simcoe: {
      type: 'checkbox',
      title: `To see a specialist, we require a referral from your Family Doctor or Nurse Practitioner. If you do not have a primary care provider or are unable to access a referral from them in a timely fashion, 
      you may book a Virtual Referral with our Nurse Practitioner on the next page. Seeing our Nurse Practitioner will not negatively impact your primary care provider.<br/>   
Please note that for egg freezing and IVF, some testing and treatments are only available at our Toronto office. Our team can clarify which ones apply to you during your Virtual Referral appointment.`,
      options: [
        {
          label:
            'I understand I will need to visit the Toronto location for some testing and treatment.',
          value: 'acknowledge_toronto'
        }
      ]
    }
  };

  // Branching logic
  const getNextQuestionKeyWithData = (key, data) => {
    try {
      if (key === 'trying_to_get_pregnant') return 'treatment_interest';

      if (key === 'treatment_interest') {
        if (data.treatment_interest === 'not_sure') {
          return 'dob';
        }
        return 'location';
      }

      if (key === 'location') return 'valid_ohip';
      if (key === 'valid_ohip') return 'dob';

      if (key === 'dob') {
        const age = data.dob ? calculateAge(data.dob) : 0;
        console.log('referral source to send to eligibility function:', userTracking?.referral_source, userTracking);
        const eligibility = readyTobookEligibity(
          age,
          data.valid_ohip,
          data.location,
          null,
          null,
          data.dob,
          userTracking?.referral_source,
          data.treatment_interest
        );

        data.eligibility = eligibility;

        if (eligibility === 'VR') {
          if (data.location === 'Simcoe') {
            return 'toronto_acknowledge_vr_simcoe';
          }
          if (data.location === 'Ottawa') {
            return 'toronto_acknowledge_vr_ottawa';
          }
          return 'acuity_link';
        }

        if (eligibility === 'OTHER') return 'acuity_link';

        if (eligibility === 'EF') return 'acuity_link';

        if (['NCP'].includes(eligibility)) {
          if (data.location === 'Simcoe') {
            return 'toronto_acknowledge_ncp_simcoe';
          }
          return 'acuity_link';
        }

        return 'demographic_form';
      }

      if (
        [
          'toronto_acknowledge_ncp_simcoe',
          'toronto_acknowledge_vr_ottawa',
          'toronto_acknowledge_vr_simcoe'
        ].includes(key)
      ) {
        return 'acuity_link';
      }

      throw new Error(`Unhandled branching case: key="${key}"`);
    } catch (error) {
      console.error('❌ Error in getNextQuestionKeyWithData:', error);
      return 'demographic_form';
    }
  };

  // Save current state and move forward
  const goNext = () => {
    // throw new Error('error error error');

    const nextKey = getNextQuestionKeyWithData(currentKey, formData);
    setHistory([...history, { key: currentKey, data: { ...formData } }]);
    // setCurrentKey(nextKey);

    if (nextKey === 'acuity_link') {
      const link = getAcuitylinknew(formData);

      if (!link || link === 'NONE') {
        console.error('❌ No valid Acuity link generated:', link);
        window.alert('Something went wrong generating your booking link.');
        return;
      }
      const encodedBlobName = encodeURIComponent(specificBlobName);
      const delimiter = link.includes('?') ? '&' : '?';
      const finalLink = `${link}${delimiter}${import.meta.env.VITE_ACUITY_BLOB_NAME_FIELD}=${encodedBlobName}`;
      const updatedFormdata = { ...formData, ...userTracking };

      console.log(1111111, updatedFormdata);

      saveIntakeQuestionnaireDataNew({
        formData: updatedFormdata,
        blobPath,
        specificBlobName,
        workflow: 'acuityFlow',
        userTracking
      })
        .then(() => {
          window.top.location.href = finalLink;
        })
        .catch(error => {
          console.error('❌ Error saving data before Acuity redirect:', error);
          window.alert(
            'Something went wrong saving your data. Please try again.'
          );
        });
      return;
    }
    setCurrentKey(nextKey);
  };

  // Restore previous snapshot
  const goBack = () => {
    if (history.length === 0) return;
    const lastSnapshot = history[history.length - 1];
    setFormData(lastSnapshot.data);
    setCurrentKey(lastSnapshot.key);
    setHistory(history.slice(0, -1));
  };

  const currentQuestion =
    currentKey === 'treatment_interest'
      ? {
          type: 'choice',
          title: 'Which treatment are you interested in exploring?',
          options: getTreatmentInterestOptions(formData.trying_to_get_pregnant)
        }
      : questions[currentKey];

  // 🧠 Smarter handleChange: only reset when value truly changes
  const handleChange = (key, value) => {
    if (formData[key] === value) {
      return formData; // no change; return current state so callers stay in sync
    }

    let updatedData = { ...formData, [key]: value };

    // Reset dependent answers only if parent changed
    if (dependentKeys[key]) {
      dependentKeys[key].forEach(depKey => {
        updatedData[depKey] = Array.isArray(updatedData[depKey]) ? [] : null;
      });
    }

    // 🧮 If DOB changed → update age immediately
    if (key === 'dob') {
      updatedData.age = value ? calculateAge(value) : null;

      // Also recalc eligibility right away
      updatedData.eligibility = readyTobookEligibity(
        updatedData.age,
        updatedData.valid_ohip,
        updatedData.location,
        null,
        null,
        updatedData.dob,
        userTracking?.referral_source,
        updatedData.treatment_interest
      );

      console.log('🎂 Age set:', updatedData.age);
      console.log('🔁 Eligibility recalculated:', updatedData.eligibility);
    }

    setFormData(updatedData);
    return updatedData;
  };
  const handleFormsubmit = async e => {
    console.log(111111, 'validation run');
    e.preventDefault();
    toast.dismiss();
    if (!validateAndScroll(e.target, setShowFormErrors)) {
      console.log(2222222, 'validation run 22222');
      return;
    }
    console.log(333333, 'validation run 3333');
    setShowFormErrors(false);
    setIsLoading(true);
    // throw new Error('error error error');
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const blobPath = `${import.meta.env.VITE_RAW_FILE_PATH}`;
      const specificBlobName = generateUniqueId();
      const updatedFormdata = { ...formData, ...userTracking };
      await saveIntakeQuestionnaireDataNew({
        formData: updatedFormdata,
        blobPath,
        specificBlobName,
        workflow: 'formComplete',
        userTracking
      });
      window.top.location.href =
        'https://www.hannamfertility.com/thank-you-form';
    } catch (error) {
      console.error('Error during form submission:', error);
      Sentry.captureException(error);
      toast.dismiss();
      toast.error('Sorry, something went wrong on our end. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // 🧭 Demographic form section
  if (currentKey === 'demographic_form') {
    return (
      <Container className="form-intake-questionnaire my-4">
        <Form
          onSubmit={handleFormsubmit}
          className={`intake-form-p2 ${showFormErrors ? 'validated' : ''}`}
          noValidate
        >
          <div className="form-section mt-5">
            <DemographicForm
              formData={formData}
              setFormData={setFormData}
              formErrors={{}}
              showFormErrors={false}
            />
          </div>
          {formData.partner_yes_no === 'Yes' && (
            <Container className="form-section mt-5">
              <DemographicPartnerForm
                formData={formData}
                setFormData={setFormData}
                formErrors={{}}
                showFormErrors={false}
              />
            </Container>
          )}

          <div className="form-section mt-5">
            <AdditionalInformation
              formData={formData}
              setFormData={setFormData}
              formErrors={{}}
            />
          </div>

          <div className="form-section mt-5">
            <TermAndCondition
              formData={formData}
              setFormData={setFormData}
              formErrors={{}}
            />
          </div>
          <div className="d-flex justify-content-between mt-4">
            <Button variant="secondary" onClick={goBack}>
              Back
            </Button>
            <Button variant="primary" type="submit">
              Submit Intake
            </Button>
          </div>
        </Form>
      </Container>
    );
  }

  return (
    <Container className="form-intake-questionnaire my-4">
      {currentQuestion && currentKey !== 'demographic_form' && (
        <>
          {/* 🟣 Progress Dots above question title */}
          <ProgressDots total={5} activeIndex={history.length} />
          {currentQuestion.type === 'choice' ? (
            <QuestionField
              title={currentQuestion.title}
              items={currentQuestion.options}
              selected={formData[currentKey]}
              onChange={val => {
                const updatedData = handleChange(currentKey, val);
                const nextKey = getNextQuestionKeyWithData(
                  currentKey,
                  updatedData
                );

                if (nextKey === 'acuity_link') {
                  const link = getAcuitylinknew(updatedData);

                  if (!link || link === 'NONE') {
                    console.error('❌ No valid Acuity link generated:', link);
                    window.alert(
                      'Something went wrong generating your booking link.'
                    );
                    return;
                  }

                  const encodedBlobName = encodeURIComponent(specificBlobName);
                  const delimiter = link.includes('?') ? '&' : '?';
                  const finalLink = `${link}${delimiter}${import.meta.env.VITE_ACUITY_BLOB_NAME_FIELD}=${encodedBlobName}`;
                  const updatedFormdata = { ...updatedData, ...userTracking };

                  saveIntakeQuestionnaireDataNew({
                    formData: updatedFormdata,
                    blobPath,
                    specificBlobName,
                    workflow: 'acuityFlow',
                    userTracking
                  })
                    .then(() => {
                      window.top.location.href = finalLink;
                    })
                    .catch(error => {
                      console.error(
                        '❌ Error saving data before Acuity redirect:',
                        error
                      );
                      window.alert(
                        'Something went wrong saving your data. Please try again.'
                      );
                    });

                  return;
                }

                // ✅ Always save full snapshot for back navigation
                if (nextKey)
                  setHistory([
                    ...history,
                    { key: currentKey, data: updatedData }
                  ]);
                setCurrentKey(nextKey);
              }}
            />
          ) : currentQuestion.type === 'checkbox' ? (
            <CheckboxField
              title={currentQuestion.title}
              items={currentQuestion.options}
              selectedValues={formData[currentKey]}
              onChange={vals => handleChange(currentKey, vals)}
            />
          ) : (
            <DateField
              title={currentQuestion.title}
              selected={formData[currentKey]}
              onChange={val => handleChange(currentKey, val)}
            />
          )}
        </>
      )}

      {/* Back & Next buttons */}
      <div className="button-footer d-flex justify-content-between mt-4">
        {history.length > 0 && currentKey !== 'trying_to_get_pregnant' ? (
          <button className="custom-back-button" onClick={goBack}>
            <span className="arrow">←</span> Back
          </button>
        ) : (
          <div />
        )}

        {(currentQuestion?.type === 'date' ||
          currentQuestion?.type === 'checkbox') && (
          <Button
            variant="primary"
            onClick={goNext}
            disabled={
              (currentQuestion.type === 'date' && !formData[currentKey]) ||
              (currentQuestion.type === 'checkbox' &&
                (!formData[currentKey] || formData[currentKey].length === 0))
            }
          >
            Next
          </Button>
        )}
      </div>
    </Container>
  );
}

export default SampleFormContainer;
